import { toast } from 'sonner';
import React, { useState, useRef } from 'react';
import { Upload, Download, X, ChevronRight, AlertTriangle, CheckCircle, HelpCircle, XCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { workersImportApi } from '../lib/api';

const VERDICT_META = {
  DA_CREARE:    { color: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle,  label: 'DA CREARE' },
  GIA_PRESENTE: { color: 'bg-slate-100 text-slate-700',     Icon: HelpCircle,   label: 'GIÀ PRESENTE' },
  CONFLITTO:    { color: 'bg-red-100 text-red-800',         Icon: XCircle,      label: 'CONFLITTO' },
  AMBIGUO:      { color: 'bg-amber-100 text-amber-800',     Icon: AlertTriangle, label: 'AMBIGUO' },
  ERRORE_DATI:  { color: 'bg-red-100 text-red-800',         Icon: XCircle,      label: 'ERRORE DATI' },
};

const ImportWorkersModal = ({ open, onOpenChange, companies, selectedCompanyId, onDone }) => {
  const [step, setStep] = useState(1);
  const [companyId, setCompanyId] = useState(selectedCompanyId && selectedCompanyId !== 'all' ? selectedCompanyId : '');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [filterVerdict, setFilterVerdict] = useState('ALL');
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setApplyResult(null); setError(null); setBusy(false); setFilterVerdict('ALL');
  };
  const close = () => { reset(); onOpenChange(false); };

  const currentCompany = companies?.find((c) => c.id === companyId);

  const doDownloadTemplate = async () => {
    setError(null);
    try {
      await workersImportApi.downloadTemplate(companyId);
    } catch (e) { setError(`Download template fallito: ${e?.message || e}`); }
  };

  const doPreview = async () => {
    if (!file) return setError('Seleziona un file .xlsx');
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('companyId', companyId);
      const res = await workersImportApi.preview(fd);
      setPreview(res.data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Errore preview');
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!preview?.dryRunToken) return setError('Token dry-run mancante, rifai il preview');
    const rows = preview.rows.filter((r) => r.verdict === 'DA_CREARE');
    if (rows.length === 0) return setError('Nessuna riga in DA_CREARE');
    setBusy(true); setError(null);
    try {
      const rowNumbers = rows.map((r) => r.row);
      const res = await workersImportApi.apply(companyId, preview.dryRunToken, rowNumbers);
      setApplyResult(res.data);
      if (res.data?.auto_merge?.pairs_applied) toast.info(`Unite automaticamente ${res.data.auto_merge.pairs_applied} schede doppie della stessa persona`);
      setStep(4);
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail?.error || e?.response?.data?.detail || e?.message || 'Errore apply');
    } finally { setBusy(false); }
  };

  const summary = preview?.summary;
  const rows = preview?.rows || [];
  const filteredRows = filterVerdict === 'ALL' ? rows : rows.filter((r) => r.verdict === filterVerdict);

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? close() : null)}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="import-workers-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Upload size={18} /> Importa lavoratori
            <span className="text-xs font-medium text-slate-500">Step {step}/4</span>
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="my-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="import-error">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">Azienda destinataria</label>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="import-company-select" className="w-full h-10 rounded-lg border border-slate-200 px-3">
                <option value="">— Seleziona un'azienda —</option>
                {(companies || []).filter(c => c.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">I lavoratori del file verranno associati a questa azienda. Il template <b>NON</b> contiene la colonna Azienda.</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={doDownloadTemplate} disabled={!companyId} data-testid="import-download-template" className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                <Download size={14} /> SCARICA TEMPLATE .xlsx
              </button>
              <button onClick={() => setStep(2)} disabled={!companyId} data-testid="import-goto-upload" className="px-4 py-2 rounded-full bg-[#023F80] hover:bg-[#012558] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                CONTINUA <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="text-sm text-slate-700">
              Azienda: <b>{currentCompany?.name || '—'}</b>
              <button onClick={() => setStep(1)} className="ml-3 text-xs text-blue-600 hover:underline">cambia</button>
            </div>
            <div>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" className="block text-sm" />
              {file && <p className="text-xs text-slate-500 mt-1">Selezionato: {file.name} ({Math.round(file.size/1024)} KB)</p>}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={doPreview} disabled={!file || busy} data-testid="import-run-preview" className="px-4 py-2 rounded-full bg-[#023F80] hover:bg-[#012558] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                ANALIZZA (DRY-RUN)
              </button>
              <button onClick={() => setStep(1)} className="text-xs text-slate-500 hover:underline">indietro</button>
            </div>
          </div>
        )}

        {step === 3 && summary && (
          <div className="space-y-4">
            <div className="text-sm text-slate-700">Azienda: <b>{preview.companyName}</b> · Righe file: <b>{summary.total_rows}</b></div>
            <div className="grid grid-cols-5 gap-2">
              {[
                ['DA_CREARE', summary.da_creare],
                ['GIA_PRESENTE', summary.gia_presente],
                ['CONFLITTO', summary.conflitto],
                ['AMBIGUO', summary.ambiguo],
                ['ERRORE_DATI', summary.errore_dati],
              ].map(([k, v]) => {
                const meta = VERDICT_META[k];
                return (
                  <button key={k} onClick={() => setFilterVerdict(filterVerdict === k ? 'ALL' : k)} data-testid={`filter-${k}`}
                    className={`text-left p-3 rounded-xl border ${filterVerdict === k ? 'border-slate-900' : 'border-slate-200'} hover:border-slate-400`}>
                    <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color}`}>{meta.label}</div>
                    <div className="text-2xl font-bold text-slate-800 mt-1">{v}</div>
                  </button>
                );
              })}
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Riga</th>
                    <th className="text-left px-2 py-1.5">Lavoratore</th>
                    <th className="text-left px-2 py-1.5">CF</th>
                    <th className="text-left px-2 py-1.5">Nascita</th>
                    <th className="text-left px-2 py-1.5">Verdetto</th>
                    <th className="text-left px-2 py-1.5">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const meta = VERDICT_META[r.verdict];
                    return (
                      <tr key={r.row} className="border-t border-slate-100">
                        <td className="px-2 py-1 text-slate-500">{r.row}</td>
                        <td className="px-2 py-1 font-semibold">{r.firstName} {r.lastName}</td>
                        <td className="px-2 py-1 font-mono text-[11px]">{r.fiscalCode || '—'}</td>
                        <td className="px-2 py-1">{r.birthDate || '—'}</td>
                        <td className="px-2 py-1"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color}`}>{meta.label}</span></td>
                        <td className="px-2 py-1 text-slate-600">{r.errors?.join(', ') || r.match_detail?.reason || (r.warnings || []).join(', ') || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button onClick={doApply} disabled={busy || summary.da_creare === 0} data-testid="import-run-apply" className="px-4 py-2 rounded-full bg-[#023F80] hover:bg-[#012558] text-white text-xs font-bold flex items-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                IMPORTA {summary.da_creare} LAVORATORI
              </button>
              <button onClick={() => setStep(2)} className="text-xs text-slate-500 hover:underline">indietro</button>
              <span className="text-xs text-slate-500">Verranno importate solo le righe DA_CREARE. Le altre restano nel file, non toccate.</span>
            </div>
          </div>
        )}

        {step === 4 && applyResult && (
          <div className="space-y-3" data-testid="import-result">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="font-bold text-emerald-900">Import completato</div>
              <div className="text-sm text-emerald-800 mt-1">Inseriti: <b>{applyResult.inserted}</b> · Skipped: <b>{applyResult.skipped?.length || 0}</b> · Errori: <b>{applyResult.errors?.length || 0}</b></div>
              <div className="text-xs text-emerald-700 mt-1">Backup: <code>{applyResult.backup?.collection}</code> ({applyResult.backup?.count} record)</div>
              <div className="text-xs text-emerald-700 mt-1">Workers azienda: {applyResult.counts_pre?.workers_of_company} → {applyResult.counts_post?.workers_of_company}</div>
            </div>
            {(applyResult.skipped?.length > 0 || applyResult.errors?.length > 0) && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                <div className="font-bold mb-1">Righe non importate:</div>
                {(applyResult.skipped || []).slice(0, 30).map((s, i) => <div key={`sk-${i}`}>skipped: {s.reason} — {s.row?.firstName} {s.row?.lastName}</div>)}
                {(applyResult.errors || []).slice(0, 30).map((s, i) => <div key={`er-${i}`}>errore: {s.reason} — {s.row?.firstName} {s.row?.lastName}</div>)}
              </div>
            )}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={close} data-testid="import-close" className="px-4 py-2 rounded-full bg-[#023F80] text-white text-xs font-bold">CHIUDI</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportWorkersModal;
